import { useTranslation } from "react-i18next";
import { HorizontalSingleSelectFilter } from "../../Everou/_components/HorizontalFilterDropdown";
import { IsoStartEndDatePicker } from "../../Everou/_components/IsoStartEndDatePicker";
import { TimeHelper } from "../../Everou/_helpers";
import { PMSReservationStatus, RESERVATION_STATE_BADGE } from "../_models/PMSIntegration";

// Sentinel for the "all statuses" option (no status filter applied).
export const RESERVATION_STATUS_ALL = "ALL";

// Sentinel for the "all locations" option (no location filter applied).
export const RESERVATION_LOCATION_ALL = "ALL";

// Order the status filter the same way reservations naturally flow in time.
const STATUS_FILTER_ORDER = [
    PMSReservationStatus.UPCOMING,
    PMSReservationStatus.ACTIVE,
    PMSReservationStatus.EARLY_CHECKOUT,
    PMSReservationStatus.EXPIRED,
    PMSReservationStatus.CANCELLED,
];

type ReservationFilterState = {
    statusFilter: string;
    locationFilter?: string;
    dateFrom: string | null;
    dateTo: string | null;
    timezone?: string;
    // The start filter defaults to the current instant. While untouched, send it
    // verbatim so the list starts "from now"; once the user picks a day it is
    // widened to start-of-day like the end bound.
    dateFromIsDefault?: boolean;
};

// Translate the local filter state into the backend query params. Day bounds
// are widened (from = start of day, to = end of day) so a day picked on either
// end is inclusive, matching the backend's overlap semantics. The exception is
// the untouched default start, sent as the exact current instant. Unset filters
// are left undefined and dropped by the request serializer.
export function buildReservationFilterParams({
    statusFilter,
    locationFilter,
    dateFrom,
    dateTo,
    timezone,
    dateFromIsDefault,
}: ReservationFilterState) {
    return {
        status:
            statusFilter && statusFilter !== RESERVATION_STATUS_ALL
                ? statusFilter
                : undefined,
        uid:
            locationFilter && locationFilter !== RESERVATION_LOCATION_ALL
                ? locationFilter
                : undefined,
        date_from: dateFrom
            ? dateFromIsDefault
                ? dateFrom
                : TimeHelper.getStartOfDayInTimezoneIso(dateFrom, timezone)
            : undefined,
        date_to: dateTo
            ? TimeHelper.getEndOfDayInTimezoneIso(dateTo, timezone)
            : undefined,
    };
}

type LocationOption = { uid: string; name: string };

type ReservationsFilterBarProps = {
    statusFilter: string;
    onStatusChange: (value: string) => void;
    // The location filter only makes sense in the global reservations view.
    // The per-location view omits these props and the dropdown is hidden.
    locationFilter?: string;
    onLocationChange?: (value: string) => void;
    onLocationOpen?: () => void;
    locations?: LocationOption[];
    dateFrom: string | null;
    dateTo: string | null;
    onDateFromChange: (value: string | null) => void;
    onDateToChange: (value: string | null) => void;
    onClearDates: () => void;
    timezone?: string;
};

export function ReservationsFilterBar({
    statusFilter,
    onStatusChange,
    locationFilter = RESERVATION_LOCATION_ALL,
    onLocationChange = () => {},
    onLocationOpen,
    locations,
    dateFrom,
    dateTo,
    onDateFromChange,
    onDateToChange,
    onClearDates,
    timezone,
}: ReservationsFilterBarProps) {
    const { t } = useTranslation();

    const statusOptions = [
        { value: RESERVATION_STATUS_ALL, label: t("global_all") },
        ...STATUS_FILTER_ORDER.map((state) => ({
            value: state,
            label: t(RESERVATION_STATE_BADGE[state].labelKey),
        })),
    ];

    // The global view passes a `locations` array (the per-location view omits
    // it). Show the dropdown whenever the prop is provided, even while the list
    // is still loading, so it doesn't pop in late.
    const showLocationFilter = locations !== undefined;
    const locationOptions = [
        { value: RESERVATION_LOCATION_ALL, label: t("mod_pms_reservation_filter_all") },
        ...(locations ?? []).map((location) => ({
            value: location.uid,
            label: location.name,
        })),
    ];

    const hasDateFilter = !!dateFrom || !!dateTo;

    return (
        <div
            className="mb-3 d-flex flex-wrap align-items-center"
            style={{ gap: "0.5rem" }}
        >
            <HorizontalSingleSelectFilter
                label={t("global_status")}
                options={statusOptions}
                value={statusFilter}
                onChange={onStatusChange}
                isActive={statusFilter !== RESERVATION_STATUS_ALL}
            />
            {showLocationFilter && (
                <HorizontalSingleSelectFilter
                    label={t("global_location")}
                    options={locationOptions}
                    value={locationFilter}
                    onChange={onLocationChange}
                    onOpen={onLocationOpen}
                    isActive={locationFilter !== RESERVATION_LOCATION_ALL}
                    isSearchable
                />
            )}
            <IsoStartEndDatePicker
                compact
                dateOnly
                timezone={timezone}
                startIsoDate={dateFrom}
                endIsoDate={dateTo}
                handleStartDateChange={onDateFromChange}
                handleEndDateChange={onDateToChange}
                startText={t("mod_integrations_reservation_start")}
                endText={t("mod_integrations_reservation_end")}
            />
            {hasDateFilter && (
                <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    onClick={onClearDates}
                >
                    {t("mod_pms_reservation_filter_clear_dates")}
                </button>
            )}
        </div>
    );
}
